import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, UserPlus, Upload, Link2, Mail, Shield, Search, Filter, MoreVertical, Edit, Trash2, CheckCircle, XCircle, Copy, Download, FileSpreadsheet, AlertCircle, Clock, TrendingUp, Activity } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function UserManagementPage() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userType, setUserType] = useState<'learners' | 'admins'>('learners');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('learner');
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  // Mock data
  const quickStats = [{
    label: 'Total Learners',
    value: '248',
    change: '+12',
    trend: 'up',
    icon: Users
  }, {
    label: 'Active Admins',
    value: '8',
    change: '+2',
    trend: 'up',
    icon: Shield
  }, {
    label: 'Pending Invites',
    value: '18',
    change: '-5',
    trend: 'down',
    icon: Mail
  }, {
    label: 'Registered This Month',
    value: '32',
    change: '+8',
    trend: 'up',
    icon: TrendingUp
  }];
  const learners = [{
    id: 1,
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    status: 'active',
    skills: 4,
    lastActive: '2024-01-15',
    joinDate: '2023-09-12'
  }, {
    id: 2,
    name: 'Michael Chen',
    email: 'mchen@example.com',
    status: 'active',
    skills: 3,
    lastActive: '2024-01-14',
    joinDate: '2023-10-05'
  }, {
    id: 3,
    name: 'Emily Davis',
    email: 'emily.d@example.com',
    status: 'active',
    skills: 2,
    lastActive: '2024-01-13',
    joinDate: '2023-11-20'
  }, {
    id: 4,
    name: 'John Smith',
    email: 'john.smith@example.com',
    status: 'inactive',
    skills: 2,
    lastActive: '2023-12-28',
    joinDate: '2023-08-15'
  }, {
    id: 5,
    name: 'Lisa Wong',
    email: 'lisa.w@example.com',
    status: 'pending',
    skills: 0,
    lastActive: 'Never',
    joinDate: '2024-01-10'
  }];
  const admins = [{
    id: 1,
    name: 'Linda Taylor',
    email: 'linda.taylor@example.com',
    status: 'active',
    role: 'Super Admin',
    lastActive: '2024-01-15',
    joinDate: '2023-01-10'
  }, {
    id: 2,
    name: 'Robert Martinez',
    email: 'robert.m@example.com',
    status: 'active',
    role: 'Admin',
    lastActive: '2024-01-14',
    joinDate: '2023-03-22'
  }, {
    id: 3,
    name: 'Jennifer Lee',
    email: 'jennifer.lee@example.com',
    status: 'active',
    role: 'Admin',
    lastActive: '2024-01-13',
    joinDate: '2023-05-18'
  }];
  const filteredUsers = (userType === 'learners' ? learners : admins).filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) || user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || user.status === selectedFilter;
    return matchesSearch && matchesFilter;
  });
  const handleGenerateLink = () => {
    const linkType = userType === 'learners' ? 'learner' : 'admin';
    const newLink = `https://platform.example.com/register/${linkType}?token=${Math.random().toString(36).substr(2, 9)}`;
    setGeneratedLink(newLink);
  };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };
  const handleAddUser = () => {
    // Handle user addition logic
    setShowAddModal(false);
    setFirstName('');
    setLastName('');
    setEmail('');
    setRole('learner');
  };
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2060">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2061">
        <div className="max-w-[1440px] mx-auto px-8" data-id="element-2062">
          <div className="flex items-center h-16" data-id="element-2063">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2064">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-8 ml-12" data-id="element-2065">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2066">
                <Home size={18} data-id="element-2067" />
                <span data-id="element-2068">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2069">
                <BarChart3 size={18} data-id="element-2070" />
                <span data-id="element-2071">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2072">
                <FileText size={18} data-id="element-2073" />
                <span data-id="element-2074">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-2075">
                <UserCog size={18} data-id="element-2076" />
                <span data-id="element-2077">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-4 ml-auto" data-id="element-2078">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2079">
                <Bell size={20} data-id="element-2080" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2081"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2082">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2083">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2084">
                    LT
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-2085">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2086" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2087">
                    <div className="py-2" data-id="element-2088">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2089">
                        <Users size={18} className="text-gray-600" data-id="element-2090" />
                        <span className="font-medium text-gray-900" data-id="element-2091">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2092">
                        <Home size={18} className="text-gray-600" data-id="element-2093" />
                        <span className="font-medium text-gray-900" data-id="element-2094">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2095">
                        <UserCog size={18} className="text-gray-600" data-id="element-2096" />
                        <span className="font-medium text-gray-900" data-id="element-2097">
                          Admin Hub
                        </span>
                      </button>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2098">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2099">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-2100"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-2101"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2102">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2103"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2104">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2105">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2106"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2107"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2108"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2109">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-8 py-8" data-id="element-2110">
        {/* Header with Toggle */}
        <div className="mb-8" data-id="element-2111">
          <div className="flex items-center justify-between mb-4" data-id="element-2112">
            <div data-id="element-2113">
              <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2114">
                User Management
              </h1>
              <p className="text-gray-600" data-id="element-2115">
                Manage {userType === 'learners' ? 'learners' : 'administrators'}{' '}
                across your platform
              </p>
            </div>

            {/* User Type Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-id="element-2116">
              <button onClick={() => setUserType('learners')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${userType === 'learners' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2117">
                <span className="flex items-center gap-2" data-id="element-2118">
                  <Users size={16} data-id="element-2119" />
                  Learners
                </span>
              </button>
              <button onClick={() => setUserType('admins')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${userType === 'admins' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2120">
                <span className="flex items-center gap-2" data-id="element-2121">
                  <Shield size={16} data-id="element-2122" />
                  Admins
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-6 mb-8" data-id="element-2123">
          {quickStats.map((stat, index) => {
          const Icon = stat.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-2124">
                <div className="flex items-center justify-between mb-2" data-id="element-2125">
                  <span className="text-sm font-medium text-gray-600" data-id="element-2126">
                    {stat.label}
                  </span>
                  {stat.trend === 'up' ? <TrendingUp size={20} className="text-green-600" data-id="element-2127" /> : stat.trend === 'down' ? <TrendingUp size={20} className="text-red-600 rotate-180" data-id="element-2128" /> : <Activity size={20} className="text-gray-400" data-id="element-2129" />}
                </div>
                <div className="flex items-end justify-between" data-id="element-2130">
                  <div className="text-3xl font-bold text-gray-900" data-id="element-2131">
                    {stat.value}
                  </div>
                  <span className={`text-lg font-bold ${stat.trend === 'up' ? 'text-green-600' : stat.trend === 'down' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-2132">
                    {stat.change}
                  </span>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-6 mb-8" data-id="element-2133">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} onClick={() => setShowAddModal(true)} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2134">
            <div className="flex items-center gap-4" data-id="element-2135">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#30718D] to-[#265a70] flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2136">
                <UserPlus size={24} className="text-white" data-id="element-2137" />
              </div>
              <div className="flex-1" data-id="element-2138">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#30718D] transition-colors" data-id="element-2139">
                  Add Individual {userType === 'learners' ? 'Learner' : 'Admin'}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2140">
                  Register a single{' '}
                  {userType === 'learners' ? 'learner' : 'administrator'}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} onClick={() => setShowBulkModal(true)} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2141">
            <div className="flex items-center gap-4" data-id="element-2142">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007558] to-[#005a44] flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2143">
                <Upload size={24} className="text-white" data-id="element-2144" />
              </div>
              <div className="flex-1" data-id="element-2145">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#007558] transition-colors" data-id="element-2146">
                  Bulk Upload {userType === 'learners' ? 'Learners' : 'Admins'}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2147">
                  Import multiple{' '}
                  {userType === 'learners' ? 'learners' : 'admins'} via CSV
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} onClick={() => {
          setShowLinkModal(true);
          handleGenerateLink();
        }} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2148">
            <div className="flex items-center gap-4" data-id="element-2149">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2150">
                <Link2 size={24} className="text-white" data-id="element-2151" />
              </div>
              <div className="flex-1" data-id="element-2152">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors" data-id="element-2153">
                  Generate {userType === 'learners' ? 'Learner' : 'Admin'} Link
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2154">
                  Create self-registration URL
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* User List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-2155">
          <div className="p-6 border-b border-gray-200" data-id="element-2156">
            <div className="flex items-center justify-between mb-4" data-id="element-2157">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-2158">
                {userType === 'learners' ? 'All Learners' : 'All Administrators'}{' '}
                ({filteredUsers.length})
              </h2>
            </div>

            {/* Search and Filter */}
            <div className="flex items-center gap-4" data-id="element-2159">
              <div className="flex-1 relative" data-id="element-2160">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2161" />
                <input type="text" placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent text-sm" data-id="element-2162" />
              </div>
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1" data-id="element-2163">
                <button onClick={() => setSelectedFilter('all')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedFilter === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2164">
                  All
                </button>
                <button onClick={() => setSelectedFilter('active')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedFilter === 'active' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2165">
                  Active
                </button>
                <button onClick={() => setSelectedFilter('inactive')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedFilter === 'inactive' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2166">
                  Inactive
                </button>
                <button onClick={() => setSelectedFilter('pending')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedFilter === 'pending' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2167">
                  Pending
                </button>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto" data-id="element-2168">
            <table className="w-full" data-id="element-2169">
              <thead data-id="element-2170">
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2171">
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-2172">
                    Name
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-2173">
                    Email
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-2174">
                    Status
                  </th>
                  {userType === 'learners' ? <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-2175">
                      Skills
                    </th> : <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-2176">
                      Role
                    </th>}
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-2177">
                    Last Active
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-2178">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-2179">
                <AnimatePresence mode="wait" data-id="element-2180">
                  {filteredUsers.map((user, index) => <motion.tr key={`${userType}-${user.id}`} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  delay: index * 0.05
                }} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-id="element-2181">
                      <td className="py-4 px-6" data-id="element-2182">
                        <div className="flex items-center gap-3" data-id="element-2183">
                          <div className="w-10 h-10 rounded-full bg-[#30718D] flex items-center justify-center text-white text-sm font-bold" data-id="element-2184">
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-2185">
                            {user.name}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600" data-id="element-2186">
                        {user.email}
                      </td>
                      <td className="py-4 px-6" data-id="element-2187">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${user.status === 'active' ? 'bg-[#007558]/10 text-[#007558]' : user.status === 'inactive' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-2188">
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600" data-id="element-2189">
                        {userType === 'learners' ? `${user.skills} skills` : user.role}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600" data-id="element-2190">
                        {user.lastActive}
                      </td>
                      <td className="py-4 px-6" data-id="element-2191">
                        <div className="flex items-center gap-2" data-id="element-2192">
                          <button className="p-2 text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-2193">
                            <Edit size={16} data-id="element-2194" />
                          </button>
                          <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-2195">
                            <Mail size={16} data-id="element-2196" />
                          </button>
                          <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-2197">
                            <MoreVertical size={16} data-id="element-2198" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>)}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && <div className="p-12 text-center" data-id="element-2199">
              <Users size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2200" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2201">
                No users found
              </h3>
              <p className="text-gray-600" data-id="element-2202">
                Try adjusting your search or filter criteria
              </p>
            </div>}
        </div>
      </div>

      {/* Add Individual User Modal */}
      <AnimatePresence data-id="element-2203">
        {showAddModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)} data-id="element-2204">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2205">
              <div className="flex items-center gap-3 mb-6" data-id="element-2206">
                <div className="w-10 h-10 rounded-full bg-[#30718D]/10 flex items-center justify-center" data-id="element-2207">
                  <UserPlus size={20} className="text-[#30718D]" data-id="element-2208" />
                </div>
                <h2 className="text-xl font-bold text-gray-900" data-id="element-2209">
                  Add {userType === 'learners' ? 'Learner' : 'Admin'}
                </h2>
              </div>

              <div className="space-y-4 mb-6" data-id="element-2210">
                <div data-id="element-2211">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2212">
                    First Name
                  </label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" placeholder="Enter first name" data-id="element-2213" />
                </div>
                <div data-id="element-2214">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2215">
                    Last Name
                  </label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" placeholder="Enter last name" data-id="element-2216" />
                </div>
                <div data-id="element-2217">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2218">
                    Email Address
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" placeholder="Enter email address" data-id="element-2219" />
                </div>
                {userType === 'admins' && <div data-id="element-2220">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2221">
                      Admin Role
                    </label>
                    <select value={role} onChange={e => setRole(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-2222">
                      <option value="admin" data-id="element-2223">Admin</option>
                      <option value="super-admin" data-id="element-2224">Super Admin</option>
                    </select>
                  </div>}
              </div>

              <div className="flex gap-3" data-id="element-2225">
                <button onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2226">
                  Cancel
                </button>
                <button onClick={handleAddUser} className="flex-1 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2227">
                  Add {userType === 'learners' ? 'Learner' : 'Admin'}
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Bulk Upload Modal */}
      <AnimatePresence data-id="element-2228">
        {showBulkModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkModal(false)} data-id="element-2229">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2230">
              <div className="flex items-center gap-3 mb-6" data-id="element-2231">
                <div className="w-10 h-10 rounded-full bg-[#007558]/10 flex items-center justify-center" data-id="element-2232">
                  <Upload size={20} className="text-[#007558]" data-id="element-2233" />
                </div>
                <h2 className="text-xl font-bold text-gray-900" data-id="element-2234">
                  Bulk Upload {userType === 'learners' ? 'Learners' : 'Admins'}
                </h2>
              </div>

              <div className="mb-6" data-id="element-2235">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#30718D] transition-colors cursor-pointer" data-id="element-2236">
                  <Upload size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2237" />
                  <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2238">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-2239">
                    CSV file with columns: First Name, Last Name, Email
                    {userType === 'admins' ? ', Role' : ''}
                  </p>
                </div>
              </div>

              <div className="bg-[#30718D]/5 rounded-lg p-4 mb-6" data-id="element-2240">
                <div className="flex items-start gap-3" data-id="element-2241">
                  <AlertCircle size={20} className="text-[#30718D] mt-0.5" data-id="element-2242" />
                  <div data-id="element-2243">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2244">
                      CSV Format Requirements
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-2245">
                      Your CSV must include headers: First Name, Last Name,
                      Email
                      {userType === 'admins' ? ', Role (Admin or Super Admin)' : ''}
                      . Each row represents one{' '}
                      {userType === 'learners' ? 'learner' : 'admin'}.
                    </p>
                  </div>
                </div>
              </div>

              <button className="w-full mb-3 px-4 py-2 border border-[#30718D] bg-white text-[#30718D] rounded-lg hover:bg-[#30718D]/5 transition-colors font-medium flex items-center justify-center gap-2" data-id="element-2246">
                <Download size={16} data-id="element-2247" />
                Download CSV Template
              </button>

              <div className="flex gap-3" data-id="element-2248">
                <button onClick={() => setShowBulkModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2249">
                  Cancel
                </button>
                <button className="flex-1 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2250">
                  Upload & Import
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Generate Link Modal */}
      <AnimatePresence data-id="element-2251">
        {showLinkModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowLinkModal(false)} data-id="element-2252">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2253">
              <div className="flex items-center gap-3 mb-6" data-id="element-2254">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-2255">
                  <Link2 size={20} className="text-purple-600" data-id="element-2256" />
                </div>
                <h2 className="text-xl font-bold text-gray-900" data-id="element-2257">
                  {userType === 'learners' ? 'Learner' : 'Admin'} Registration
                  Link Generated
                </h2>
              </div>

              <div className="mb-6" data-id="element-2258">
                <p className="text-sm text-gray-600 mb-4" data-id="element-2259">
                  Share this link with{' '}
                  {userType === 'learners' ? 'learners' : 'admins'} to allow
                  them to self-register on your platform.
                </p>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2260">
                  <div className="flex items-center gap-2 mb-2" data-id="element-2261">
                    <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-2262">
                      Registration Link
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${userType === 'learners' ? 'bg-[#30718D]/10 text-[#30718D]' : 'bg-purple-100 text-purple-700'}`} data-id="element-2263">
                      {userType === 'learners' ? 'Learner' : 'Admin'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-2264">
                    <code className="flex-1 text-sm text-gray-900 break-all" data-id="element-2265">
                      {generatedLink}
                    </code>
                    <button onClick={handleCopyLink} className="p-2 text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors flex-shrink-0" data-id="element-2266">
                      {linkCopied ? <CheckCircle size={18} data-id="element-2267" /> : <Copy size={18} data-id="element-2268" />}
                    </button>
                  </div>
                </div>

                {linkCopied && <motion.p initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-sm text-[#007558] mt-2 flex items-center gap-1" data-id="element-2269">
                    <CheckCircle size={14} data-id="element-2270" />
                    Link copied to clipboard!
                  </motion.p>}
              </div>

              <div className="bg-purple-50 rounded-lg p-4 mb-6" data-id="element-2271">
                <div className="flex items-start gap-3" data-id="element-2272">
                  <Clock size={20} className="text-purple-600 mt-0.5" data-id="element-2273" />
                  <div data-id="element-2274">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2275">
                      Link Expiration
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-2276">
                      This link will expire in 7 days. You can generate a new
                      link at any time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-2277">
                <button onClick={() => setShowLinkModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2278">
                  Close
                </button>
                <button onClick={handleGenerateLink} className="flex-1 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2279">
                  Generate New Link
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}